import type {Anchor, CellAddress, ItemId} from '../types'
import {type BoardPendingLanding, sitsAt} from './landing'
import {isRedundantInsertion} from './placement'

/**
 * The space a cell keeps for the card being moved, from the start of a drag until the
 * caller's data shows the drop. Both indexes point into the cell's `itemIds`.
 */
export type BoardCardRoom = {
  /**
   * Where the moved card goes in this cell: in front of the card at this index, with
   * `itemIds.length` after the last card. `null` when the card goes nowhere in this cell.
   *
   * In the cell the card came from, this equals `heldIndex` whenever a drop would leave the
   * card where it is, and is `null` once the card is headed for another cell.
   */
  index: number | null
  /** Where the moved card sits in this cell, when the drag started here. `null` in every other cell. */
  heldIndex: number | null
}

/**
 * How long cells keep room for a dropped card while the caller's data catches up. Covers a
 * move applied a task after the drop, which is how an optimistic update through TanStack
 * Query arrives, and is short enough that a refused move does not leave a gap open.
 */
export const CARD_ROOM_HOLD_MS = 100

/**
 * The room a cell keeps while a card is being dragged.
 *
 * `dropIndex` is the place a pointer over this cell aims at, and `isHeldCardLeaving` says the
 * card is headed for another cell. Both sides of the held card count as its own slot.
 */
export function draggingCardRoom({
  heldIndex,
  dropIndex,
  isHeldCardLeaving,
}: {
  heldIndex: number | null
  dropIndex: number | null
  isHeldCardLeaving: boolean
}): BoardCardRoom {
  if (heldIndex === null) return {index: dropIndex, heldIndex: null}
  if (isHeldCardLeaving) return {index: null, heldIndex}
  if (dropIndex === null || isRedundantInsertion(heldIndex, dropIndex)) return {index: heldIndex, heldIndex}

  return {index: dropIndex, heldIndex}
}

/**
 * The room a cell keeps after a drop, until the caller's data shows the move in this cell.
 *
 * `null` once the data shows it, and in every cell the move does not touch. Closing the room
 * in the same render that shows the card is what keeps the cards from jumping, so a caller
 * that applies the move a task later still lands without a flicker. A destination that already
 * held the card shows the move once the card sits at its anchor.
 *
 * `isRanked` covers a ranked cell that has loaded nothing and has more to come: its drop
 * carries no anchor, and the room it showed during the drag was at its start.
 */
export function settlingCardRoom(
  cell: CellAddress,
  itemIds: readonly ItemId[],
  {card, from, anchor}: BoardPendingLanding,
  {isRanked}: {isRanked: boolean},
): BoardCardRoom | null {
  const isFrom = isSameCell(cell, from)
  const isTo = isSameCell(cell, card)
  if (!isFrom && !isTo) return null

  const index = itemIds.indexOf(card.itemId)

  if (isFrom && isTo) {
    if (index === -1 || anchor === undefined || sitsAt(itemIds, index, anchor)) return null

    const target = anchorIndex(itemIds, anchor)
    return target === null ? null : draggingCardRoom({heldIndex: index, dropIndex: target, isHeldCardLeaving: false})
  }

  if (isFrom) return index === -1 ? null : {index: null, heldIndex: index}

  // A field that takes several values can already draw the card here, so the room stays where the
  // drag opened it until the card sits at its anchor.
  if (index !== -1 && (anchor === undefined || sitsAt(itemIds, index, anchor))) return null

  const target = anchor === undefined ? (isRanked && itemIds.length === 0 ? 0 : null) : anchorIndex(itemIds, anchor)
  return target === null ? null : {index: target, heldIndex: null}
}

/** The index an anchor names among `itemIds`, or `null` when the card it names is gone. */
function anchorIndex(itemIds: readonly ItemId[], anchor: Anchor): number | null {
  if ('before' in anchor) {
    const index = itemIds.indexOf(anchor.before)
    return index === -1 ? null : index
  }

  if ('after' in anchor) {
    const index = itemIds.indexOf(anchor.after)
    return index === -1 ? null : index + 1
  }

  return anchor.at === 'start' ? 0 : itemIds.length
}

function isSameCell(a: CellAddress, b: CellAddress): boolean {
  return a.columnKey === b.columnKey && a.rowKey === b.rowKey
}
